import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LabelList,
} from "recharts";
import React from "react";

type ChartData = {
  name: string;
  value: number;
};

interface BarChartComponentProps {
  title: string;
  color: string;
  data: ChartData[];
  showLegend?: boolean;
  barSize?: number;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
  color: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  color,
}) => {
  if (active && payload && payload.length) {
    const { name, value } = payload[0].payload;
    return (
      <div
        className="rounded px-3 py-1 text-sm text-white font-medium shadow"
        style={{
          backgroundColor: color,
          border: `1px solid ${color}`,
          minWidth: "120px",
          textAlign: "center",
        }}
      >
        <div className="font-bold">{name}</div>
        <div>{value.toLocaleString()}</div>
      </div>
    );
  }
  return null;
};

// Custom tick to wrap long text into multiple lines
const WrappedTick = ({ x, y, payload }: any) => {
  const words = payload.value.split(" ");
  return (
    <g transform={`translate(${x},${y + 10})`}>
      {words.map((word: string, index: number) => (
        <text
          key={index}
          x={0}
          y={index * 12}
          textAnchor="middle"
          fill="#666"
          fontSize={10}
        >
          {word}
        </text>
      ))}
    </g>
  );
};

const LineChart: React.FC<BarChartComponentProps> = ({
  title,
  color,
  data,
  showLegend = false,
  barSize = 40,
}) => {
  const chartWidth = Math.max(data.length * (barSize + 40), 500);
  const fontSize = data.length > 10 ? 10 : 12;

  return (
    <div className="w-full bg-white shadow rounded-md border border-gray-300 pt-4 ">
      <h2 className="text-sm font-semibold text-gray-600 px-5">{title}</h2>

      <div
        style={{ width: "95%" }}
        className="h-4 border-b border-gray-300 mb-5 mx-auto"
      ></div>

      <div className="w-full overflow-x-auto ">
        <div style={{ width: chartWidth, minWidth: "100%" }}>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart
              data={data}
              margin={{ top: 25, right: 20, left: 0, bottom: 25 }} // ⬅️ reduced bottom margin
            >
              <XAxis
                dataKey="name"
                tick={<WrappedTick />}
                tickLine={false}
                axisLine={false}
                interval={0}
              />
              <YAxis
                tick={{ fontSize: fontSize }}
                tickLine={false}
                axisLine={false}
              />
              {/* <Tooltip
                content={<CustomTooltip color={color} />}
                cursor={{ fill: "transparent" }}
              /> */}
              {showLegend && (
                <Legend
                  iconType="circle"
                  iconSize={10}
                  wrapperStyle={{ fontSize: "12px" }}
                />
              )}
              <Bar
                dataKey="value"
                fill={color}
                radius={[4, 4, 0, 0]}
                barSize={barSize}
                animationDuration={1500}
              >
                <LabelList
                  dataKey="value"
                  position="top"
                  fill="#343C6A"
                  fontSize={fontSize}
                  offset={12}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default LineChart;
